import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DollarSign, ArrowUpCircle, ArrowDownCircle, Send, 
  LogOut, User, Bell, MessageSquare, CreditCard,
  Home, Mail, Phone, Lock, Printer, X, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { userAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { internationalBanks } from '../data/internationalBanks';
import { Checkbox } from "../components/ui/checkbox";
import Overview from '../components/dashboard/Overview';
import MoveMoney from '../components/dashboard/MoveMoney';
import WireTransfer from '../components/dashboard/WireTransfer';
import Settings from '../components/dashboard/Settings';
import OTPModal from '../components/OTPModal';
import SessionTimeoutModal from '../components/SessionTimeoutModal';
import sessionManager from '../utils/sessionManager';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";

const UserDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTheme, setSelectedTheme] = useState(localStorage.getItem('userTheme') || 'blue');
  
  const themes = {
    blue: {
      name: 'Ocean Blue',
      header: 'from-blue-600 to-blue-700',
      headerBorder: 'border-blue-800',
      activeTab: 'border-blue-600 text-blue-600',
      button: 'bg-blue-600 hover:bg-blue-700',
      background: 'bg-gray-700'
    },
    green: {
      name: 'Forest Green',
      header: 'from-green-600 to-green-700',
      headerBorder: 'border-green-800',
      activeTab: 'border-green-600 text-green-600',
      button: 'bg-green-600 hover:bg-green-700',
      background: 'bg-gray-700'
    },
    purple: {
      name: 'Royal Purple',
      header: 'from-purple-600 to-purple-700',
      headerBorder: 'border-purple-800',
      activeTab: 'border-purple-600 text-purple-600',
      button: 'bg-purple-600 hover:bg-purple-700',
      background: 'bg-gray-700'
    },
    indigo: {
      name: 'Deep Indigo',
      header: 'from-indigo-600 to-indigo-700',
      headerBorder: 'border-indigo-800',
      activeTab: 'border-indigo-600 text-indigo-600',
      button: 'bg-indigo-600 hover:bg-indigo-700',
      background: 'bg-gray-700'
    },
    red: {
      name: 'Crimson Red',
      header: 'from-red-600 to-red-700',
      headerBorder: 'border-red-800',
      activeTab: 'border-red-600 text-red-600',
      button: 'bg-red-600 hover:bg-red-700',
      background: 'bg-gray-700'
    },
    slate: {
      name: 'Slate Gray',
      header: 'from-slate-700 to-slate-800',
      headerBorder: 'border-slate-900',
      activeTab: 'border-slate-600 text-slate-600',
      button: 'bg-slate-600 hover:bg-slate-700',
      background: 'bg-gray-700'
    }
  };
  
  const currentTheme = themes[selectedTheme];
  
  const handleThemeChange = (theme) => {
    setSelectedTheme(theme);
    localStorage.setItem('userTheme', theme);
    toast({
      title: "Theme Updated",
      description: `Switched to ${themes[theme].name} theme`,
    });
  };
  
  const [transferData, setTransferData] = useState({
    toAccountNumber: '',
    routingNumber: '',
    bankName: '',
    recipientName: '',
    recipientAddress: '',
    bankAddress: '',
    amount: '',
    swiftCode: '',
    iban: '',
    intermediaryBankName: '',
    intermediarySwiftCode: '',
    intermediaryBankAddress: ''
  });

  const [profileUpdate, setProfileUpdate] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    homeAddress: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [messageText, setMessageText] = useState('');
  const [wireTransferType, setWireTransferType] = useState('domestic'); // domestic or international
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [useManualBankEntry, setUseManualBankEntry] = useState(false);
  const [showIntermediaryBank, setShowIntermediaryBank] = useState(false);
  
  // Intelligent wire transfer features
  const [routingValidation, setRoutingValidation] = useState({ valid: null, bankName: '', message: '' });
  const [transferFee, setTransferFee] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  
  // OTP for wire transfer
  const [showTransferOTP, setShowTransferOTP] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [pendingTransferData, setPendingTransferData] = useState(null);
  
  // Session timeout
  const [showSessionWarning, setShowSessionWarning] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Routing number validation using ABA checksum algorithm
  const validateRoutingNumber = (routing) => {
    if (!routing || routing.length !== 9) {
      setRoutingValidation({ valid: false, bankName: '', message: 'Routing number must be 9 digits' });
      return false;
    }

    // ABA routing number checksum validation
    const digits = routing.split('').map(Number);
    const checksum = (3 * (digits[0] + digits[3] + digits[6]) +
                      7 * (digits[1] + digits[4] + digits[7]) +
                      (digits[2] + digits[5] + digits[8])) % 10;

    if (checksum !== 0) {
      setRoutingValidation({ valid: false, bankName: '', message: 'Invalid routing number format' });
      return false;
    }

    // Simulated bank lookup (in real app, would call API)
    const bankDatabase = {
      '021000021': 'JPMorgan Chase Bank',
      '026009593': 'Bank of America',
      '111000025': 'Wells Fargo Bank',
      '121000248': 'Wells Fargo Bank (California)',
      '322271627': 'Chase Bank',
      '011401533': 'TD Bank',
      '031201360': 'Citibank',
      '044000037': 'PNC Bank',
      '063100277': 'U.S. Bank',
      '122105278': 'Morgan Stanley Private Bank'
    };

    const bankName = bankDatabase[routing] || 'Valid US Bank';
    setRoutingValidation({ valid: true, bankName, message: `✓ ${bankName} verified` });
    return true;
  };

  // Calculate transfer fee and total
  const calculateTransferFee = (amount, type) => {
    const amt = parseFloat(amount) || 0;
    let fee = 0;
    
    if (type === 'domestic') {
      fee = amt * 0.001; // 0.1% fee for domestic (min $5, max $25)
      fee = Math.max(5, Math.min(25, fee));
    } else if (type === 'international') {
      fee = amt * 0.015; // 1.5% fee for international (min $25, max $75)
      fee = Math.max(25, Math.min(75, fee));
    }
    
    setTransferFee(fee);
    setTotalAmount(amt + fee);
    return fee;
  };

  // Handle routing number change
  const handleRoutingChange = (value) => {
    setTransferData({...transferData, routingNumber: value});
    if (value.length === 9) {
      validateRoutingNumber(value);
    } else {
      setRoutingValidation({ valid: null, bankName: '', message: '' });
    }
  };

  // Handle amount change
  const handleAmountChange = (value, type = 'domestic') => {
    setTransferData({...transferData, amount: value});
    calculateTransferFee(value, type);
  };

  useEffect(() => {
    fetchDashboardData();
    
    // Start session timeout manager
    sessionManager.start(
      () => setShowSessionWarning(true),  // Show warning at 14 minutes
      () => handleSessionTimeout()        // Force logout at 15 minutes
    );
    
    // Cleanup on unmount
    return () => {
      sessionManager.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [profileData, transactionsData, notificationsData, messagesData] = await Promise.all([
        userAPI.getProfile(),
        userAPI.getTransactions(),
        userAPI.getNotifications(),
        userAPI.getMessages()
      ]);
      
      setProfile(profileData.user);
      setTransactions(transactionsData.transactions);
      setNotifications(notificationsData.notifications);
      setMessages(messagesData.messages);
      
      setProfileUpdate({
        email: profileData.user.email,
        firstName: profileData.user.firstName,
        lastName: profileData.user.lastName,
        phoneNumber: profileData.user.phoneNumber || '',
        homeAddress: profileData.user.homeAddress || ''
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch dashboard data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    
    try {
      // Store transfer data and trigger OTP
      setPendingTransferData(transferData);
      
      // Generate OTP for wire transfer
      await userAPI.generateOTP(profile.email, 'wire_transfer');
      
      toast({
        title: "Verification Required",
        description: "Please check your email for the OTP code",
      });
      
      setShowTransferOTP(true);
    } catch (error) {
      toast({
        title: "OTP Generation Failed",
        description: error.response?.data?.detail || "Please try again",
        variant: "destructive"
      });
    }
  };

  const handleTransferOTPVerify = async (otpCode) => {
    setOtpLoading(true);
    
    try {
      // Verify OTP first
      await userAPI.verifyOTP(profile.email, otpCode, 'wire_transfer');
      
      // If OTP is valid, proceed with transfer
      const response = await userAPI.createTransfer(pendingTransferData);
      
      // Store receipt data
      setReceiptData({
        reference: response.reference,
        ...pendingTransferData,
        date: new Date().toLocaleString(),
        accountNumber: profile.accountNumber,
        transferType: wireTransferType,
        newBalance: response.newBalance
      });
      
      toast({
        title: "Transfer Successful",
        description: `Reference: ${response.reference}`,
      });
      
      // Reset form and state
      setTransferData({
        toAccountNumber: '',
        routingNumber: '',
        bankName: '',
        recipientName: '',
        recipientAddress: '',
        bankAddress: '',
        amount: '',
        swiftCode: '',
        iban: '',
        intermediaryBankName: '',
        intermediarySwiftCode: '',
        intermediaryBankAddress: ''
      });
      setPendingTransferData(null);
      setShowTransferOTP(false);
      setOtpLoading(false);
      setShowReceipt(true);
      fetchDashboardData();
    } catch (error) {
      setOtpLoading(false);
      throw error; // Let OTPModal handle the error display
    }
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    try {
      await userAPI.updateProfile(profileUpdate);
      toast({
        title: "Profile Updated",
        description: "Your profile has been updated successfully",
      });
      fetchDashboardData();
    } catch (error) {
      toast({
        title: "Update Failed",
        description: "Failed to update profile",
        variant: "destructive"
      });
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match",
        variant: "destructive"
      });
      return;
    }
    
    try {
      await userAPI.changePassword(passwordData.currentPassword, passwordData.newPassword);
      toast({
        title: "Password Changed",
        description: "Your password has been changed successfully",
      });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast({
        title: "Change Failed",
        description: error.response?.data?.detail || "Failed to change password",
        variant: "destructive"
      });
    }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "File size must be less than 2MB",
        variant: "destructive"
      });
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Error",
        description: "Only JPG, PNG, GIF, and WebP images are allowed",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await userAPI.uploadProfilePicture(file);
      toast({
        title: "Success",
        description: "Profile picture uploaded successfully",
      });
      fetchDashboardData(); // Refresh profile to show new picture
    } catch (error) {
      toast({
        title: "Upload Failed",
        description: error.response?.data?.detail || "Failed to upload profile picture",
        variant: "destructive"
      });
    }
  };

  const handleRemoveProfilePicture = async () => {
    try {
      await userAPI.updateProfile({ profilePicture: null });
      toast({
        title: "Success",
        description: "Profile picture removed successfully",
      });
      fetchDashboardData(); // Refresh profile
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove profile picture",
        variant: "destructive"
      });
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    try {
      await userAPI.sendMessage(messageText);
      toast({
        title: "Message Sent",
        description: "Your message has been sent to customer support",
      });
      setMessageText('');
      fetchDashboardData();
    } catch (error) {
      toast({
        title: "Send Failed",
        description: "Failed to send message",
        variant: "destructive"
      });
    }
  };

  const handleLogout = () => {
    sessionManager.stop();
    logout();
    navigate('/user/login');
  };
  
  const handleSessionTimeout = () => {
    toast({
      title: "Session Expired",
      description: "You have been logged out due to inactivity",
      variant: "destructive"
    });
    handleLogout();
  };
  
  const handleExtendSession = () => {
    setShowSessionWarning(false);
    sessionManager.extendSession();
    toast({
      title: "Session Extended",
      description: "Your session has been extended",
    });
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await userAPI.deleteNotification(notificationId);
      setNotifications(notifications.filter(n => n.id !== notificationId));
      toast({
        title: "Notification Deleted",
        description: "Notification removed successfully",
      });
    } catch (error) {
      toast({
        title: "Delete Failed",
        description: "Failed to delete notification",
        variant: "destructive"
      });
    }
  };

  if (loading || !profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${currentTheme.background} relative`}>
      {/* Background Image Overlay */}
      <div 
        className="absolute inset-0 opacity-5 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1557804506-669a67965ba0?q=80&w=1920')"
        }}
      ></div>
      
      {/* Content */}
      <div className="relative z-10">
      {/* Header */}
      <header className={`bg-gradient-to-r ${currentTheme.header} shadow-lg border-b ${currentTheme.headerBorder}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              {/* Profile Picture/Avatar */}
              <div className="flex-shrink-0">
                <div className={`w-12 h-12 rounded-full bg-white flex items-center justify-center font-bold text-lg shadow-md ${selectedTheme === 'blue' ? 'text-blue-600' : selectedTheme === 'green' ? 'text-green-600' : selectedTheme === 'purple' ? 'text-purple-600' : selectedTheme === 'indigo' ? 'text-indigo-600' : selectedTheme === 'red' ? 'text-red-600' : 'text-slate-600'}`}>
                  {profile.firstName?.[0]}{profile.lastName?.[0]}
                </div>
              </div>
              
              {/* Profile Info */}
              <div>
                <h1 className="text-xl font-bold text-white">
                  {profile.firstName} {profile.lastName}
                </h1>
                <p className="text-sm text-blue-100">Account: {profile.accountNumber}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Popover open={showNotifications} onOpenChange={setShowNotifications}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="relative bg-white/10 border-white/30 text-white hover:bg-white/20">
                    <Bell className="w-4 h-4" />
                    {notifications.filter(n => !n.read).length > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
                        {notifications.filter(n => !n.read).length}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="end">
                  <div className="p-4 border-b">
                    <h3 className="font-semibold">Notifications</h3>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        No notifications
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <div key={notif.id} className={`p-4 border-b hover:bg-gray-50 ${!notif.read ? 'bg-blue-50' : ''}`}>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="font-medium text-sm text-gray-900">{notif.title}</p>
                              <p className="text-sm text-gray-600 mt-1">{notif.message}</p>
                              <p className="text-xs text-gray-500 mt-2">{new Date(notif.createdAt).toLocaleString()}</p>
                            </div>
                            <button
                              onClick={() => handleDeleteNotification(notif.id)}
                              className="ml-2 text-gray-400 hover:text-red-600"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              <Button onClick={handleLogout} variant="outline" className="flex items-center bg-white/10 border-white/30 text-white hover:bg-white/20">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="flex space-x-4 border-b border-blue-200 bg-white/80 backdrop-blur-sm rounded-t-lg px-4">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 font-medium whitespace-nowrap transition-colors ${activeTab === 'overview' ? currentTheme.activeTab : 'text-gray-600 hover:text-blue-600'}`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('movemoney')}
            className={`px-4 py-2 font-medium whitespace-nowrap transition-colors ${activeTab === 'movemoney' ? currentTheme.activeTab : 'text-gray-600 hover:text-blue-600'}`}
          >
            Move Money
          </button>
          <button
            onClick={() => setActiveTab('transfer')}
            className={`px-4 py-2 font-medium whitespace-nowrap transition-colors ${activeTab === 'transfer' ? currentTheme.activeTab : 'text-gray-600 hover:text-blue-600'}`}
          >
            Wire Transfer
          </button>
          <button
            onClick={() => setActiveTab('transactions')}
            className={`px-4 py-2 font-medium whitespace-nowrap transition-colors ${activeTab === 'transactions' ? currentTheme.activeTab : 'text-gray-600 hover:text-blue-600'}`}
          >
            Transactions
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-4 py-2 font-medium whitespace-nowrap transition-colors ${activeTab === 'settings' ? currentTheme.activeTab : 'text-gray-600 hover:text-blue-600'}`}
          >
            Settings
          </button>
          <button
            onClick={() => setActiveTab('support')}
            className={`px-4 py-2 font-medium whitespace-nowrap transition-colors ${activeTab === 'support' ? currentTheme.activeTab : 'text-gray-600 hover:text-blue-600'}`}
          >
            Support
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <Overview 
            profile={profile}
            transactions={transactions}
            notifications={notifications}
            setActiveTab={setActiveTab}
          />
        )}

        {/* MOVE MONEY Tab */}
        {activeTab === 'movemoney' && (
          <MoveMoney 
            profile={profile}
            fetchProfile={fetchDashboardData}
          />
        )}

        {/* Wire Transfer Tab */}
        {activeTab === 'transfer' && (
          <WireTransfer
            profile={profile}
            fetchProfile={fetchDashboardData}
            onOTPRequired={(data) => {
              setPendingTransferData(data);
              userAPI.generateOTP(profile.email, 'wire_transfer').then(() => {
                toast({
                  title: "Verification Required",
                  description: "Please check your email for the OTP code",
                });
                setShowTransferOTP(true);
              }).catch((error) => {
                toast({
                  title: "OTP Generation Failed",
                  description: error.response?.data?.detail || "Please try again",
                  variant: "destructive"
                });
              });
            }}
            showReceipt={showReceipt}
            receiptData={receiptData}
            setShowReceipt={setShowReceipt}
            setActiveTab={setActiveTab}
          />
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <Card>
            <CardHeader>
              <CardTitle>Transaction History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {transactions.map((txn) => (
                      <tr key={txn.id}>
                        <td className="px-4 py-3 text-sm font-mono">{txn.reference}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            txn.type === 'credit' || txn.type === 'transfer_in' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {txn.type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">{txn.description}</td>
                        <td className={`px-4 py-3 text-sm font-medium ${
                          txn.type === 'credit' || txn.type === 'transfer_in' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {txn.type === 'credit' || txn.type === 'transfer_in' ? '+' : '-'}
                          ${txn.amount.toLocaleString('en-US', {minimumFractionDigits: 2})}
                        </td>
                        <td className="px-4 py-3 text-sm">${txn.balanceAfter.toLocaleString('en-US', {minimumFractionDigits: 2})}</td>
                        <td className="px-4 py-3 text-sm">{new Date(txn.createdAt).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <Settings
            profile={profile}
            themes={themes}
            selectedTheme={selectedTheme}
            profileUpdate={profileUpdate}
            setProfileUpdate={setProfileUpdate}
            passwordData={passwordData}
            setPasswordData={setPasswordData}
            handleThemeChange={handleThemeChange}
            handleProfileUpdate={handleProfileUpdate}
            handlePasswordChange={handlePasswordChange}
            handleProfilePictureUpload={handleProfilePictureUpload}
            handleRemoveProfilePicture={handleRemoveProfilePicture}
          />
        )}
        {/* Support Tab */}
        {activeTab === 'support' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Contact Customer Support</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSendMessage} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message
                    </label>
                    <Textarea
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      placeholder="Type your message here..."
                      required
                      rows={5}
                    />
                  </div>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Message History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {messages.map((msg) => (
                    <div key={msg.id} className={`p-4 rounded-lg ${msg.isAdminReply ? 'bg-blue-50 ml-8' : 'bg-gray-50 mr-8'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm">{msg.isAdminReply ? 'Admin' : 'You'}</span>
                        <span className="text-xs text-gray-600">{new Date(msg.createdAt).toLocaleString()}</span>
                      </div>
                      <p className="text-gray-800">{msg.message}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      </div>
      
      {/* OTP Modal for Wire Transfer */}
      <OTPModal
        isOpen={showTransferOTP}
        onClose={() => {
          setShowTransferOTP(false);
          setPendingTransferData(null);
        }}
        onVerify={handleTransferOTPVerify}
        email={profile.email}
        purpose="wire_transfer"
        isLoading={otpLoading}
      />
      
      {/* Session Timeout Warning Modal */}
      <SessionTimeoutModal
        isOpen={showSessionWarning}
        onExtend={handleExtendSession}
        onLogout={handleLogout}
      />
    </div>
  );
};

export default UserDashboard;
